/**
 * "Photos" on the project page (MB decisions 427-431; docs/specs/cloud/project-photos.md).
 *
 * - Staff: every live photo, newest first, with its category, date and place taken, who added it and where, and
 *   whether the client sees it. A manager or office staff ticks "Share with client" (decision 428); any staff
 *   member edits the caption and category, deletes a photo and adds photos (drag in or choose files). Opening a
 *   photo shows its display size; staff can also download the original. Tenant admins, managers and office staff use
 *   a photo as an aircraft's own photo (decisions 598 and 664, "Use as aircraft photo…",
 *   docs/specs/cloud/aircraft-photo.md) and, on an Aircraft photo, choose the weekly report's cover (decisions 665 and
 *   712, "Use for the weekly report cover", one photo per project; every staff member sees which photo is the cover).
 * - Clients and Preview as client: shared, live photos only, read only, never the original.
 *
 * The database decides every read and write (RLS and the storage policies); this screen only shows or hides the
 * controls and says in words what the database refused.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactElement,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  canAddPhotos,
  canDeletePhoto,
  canSetAircraftPhoto,
  canPutPhotoForward,
  canSharePhoto,
  DEFAULT_PHOTO_CATEGORY,
  photoShareState,
  PHOTO_SHARE_WORDS,
  clientVisiblePhotos,
  livePhotos,
  photoCategoryLabel,
  photoFileCheck,
  photoOriginalPlaceText,
  photoPlaceText,
  photoSizeText,
  photoTakenText,
  photoTitle,
  PHOTO_ACCEPTED_TEXT,
  PHOTO_CATEGORIES,
  type PhotoCategory,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { UseAsAircraftPhoto } from '../aircraft/UseAsAircraftPhoto.tsx';
import { UseForWeeklyCover } from '../aircraft/UseForWeeklyCover.tsx';
import type { PortalProject } from './api.ts';
import { browserPhotoRenderer, type PhotoRenderer } from './photoImages.ts';
import {
  addPhoto,
  deletePhoto,
  fetchArchivedOriginal,
  functionArchive,
  loadPhotoFiles,
  loadPhotos,
  photoOriginalPlace,
  photoPath,
  setPhotoPutForward,
  setPhotoShared,
  signedPhotoUrl,
  signedPhotoUrls,
  updatePhotoDetails,
  type ArchivePhoto,
  type OriginalPlace,
  type PhotoFiles,
  type PortalPhoto,
} from './photosApi.ts';

/** The browser work and the archive, injected by the tests. */
export interface PhotoServices {
  readonly renderer?: PhotoRenderer;
  readonly archive?: ArchivePhoto;
}

export interface PhotosTabProps {
  readonly sb: SupabaseClient;
  readonly project: Pick<PortalProject, 'id' | 'tenantId' | 'number'> & {
    readonly status?: string;
    /** Absent (undefined) in a test of the tab alone: no note then; null means no organisation is linked (R99). */
    readonly clientOrgId?: string | null;
  };
  /** Client view (client users and Preview as client) or the staff workspace. */
  readonly mode: 'staff' | 'client';
  /** Staff roles: who may add and share (display only; the database decides). */
  readonly roles?: readonly string[];
  readonly services?: PhotoServices;
}

const CAPTION_MAX = 500;

type Filter = 'all' | PhotoCategory;

const CATEGORY_OPTIONS: ReadonlyArray<readonly [PhotoCategory, string]> = PHOTO_CATEGORIES.map(
  (c) => [c.id, c.label] as const,
);

const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Whether both sizes a client is shown are in Teslyn Cloud. */
const uploaded = (files: PhotoFiles | null, id: string): boolean => {
  const f = files?.get(id);
  return Boolean(f?.has('photo_display') && f.has('photo_thumb'));
};

export function PhotosTab({ sb, project, mode, roles = [], services }: PhotosTabProps): ReactElement {
  const staff = mode === 'staff';
  // project lifecycle: an archived project's photos are read only (the database refuses changes too)
  const archived = project.status === 'archived';
  const mayAdd = staff && !archived && canAddPhotos(roles);
  const mayShare = staff && !archived && canSharePhoto(roles);
  // decision 515: any staff member puts a photo forward; managers, tenant admins and office staff delete
  const mayPutForward = staff && !archived && canPutPhotoForward(roles);
  const mayDelete = staff && !archived && canDeletePhoto(roles);
  // decisions 598 and 664: tenant admins, managers and office staff use a photo as an aircraft's own photo (an
  // archived project's too)
  const mayAircraft = staff && canSetAircraftPhoto(roles);
  // decisions 665 and 712: the same roles choose the weekly report's cover photo (not on an archived project)
  const mayCover = staff && !archived && canSetAircraftPhoto(roles);
  const [photos, setPhotos] = useState<PortalPhoto[] | null>(null);
  const [files, setFiles] = useState<PhotoFiles | null>(null);
  const [thumbs, setThumbs] = useState<ReadonlyMap<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [viewing, setViewing] = useState<PortalPhoto | null>(null);
  const [round, setRound] = useState(0);
  const reload = useCallback(() => setRound((r) => r + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    void (async () => {
      try {
        const [rows, recorded] = await Promise.all([
          loadPhotos(sb, project.id, mode),
          staff ? loadPhotoFiles(sb, project.id) : Promise.resolve(null),
        ]);
        if (!live) return;
        setPhotos(rows);
        setFiles(recorded);
        // a thumbnail is asked for only where it is in the cloud (a client's photos always have one)
        const paths = (staff ? livePhotos(rows) : clientVisiblePhotos(rows))
          .filter((p) => !recorded || recorded.get(p.id)?.has('photo_thumb'))
          .map((p) => photoPath(p, 'thumb'))
          .filter((p): p is string => p !== null);
        const urls = await signedPhotoUrls(sb, paths).catch(() => new Map<string, string>());
        if (live) setThumbs(urls);
      } catch (e: unknown) {
        if (live) setError(errText(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [sb, project.id, mode, staff, round]);

  const shown = useMemo(() => {
    if (!photos) return [];
    const visible = staff ? livePhotos(photos) : clientVisiblePhotos(photos);
    return filter === 'all' ? visible : visible.filter((p) => p.category === filter);
  }, [photos, staff, filter]);

  // decision 712: one cover photo per project, whichever aircraft flew, as the database keeps it
  const coverChanged = useCallback((id: string, cover: boolean) => {
    setPhotos((all) =>
      all
        ? all.map((p) =>
            p.id === id
              ? { ...p, weekly_cover: cover }
              : cover && p.weekly_cover
                ? { ...p, weekly_cover: false }
                : p,
          )
        : all,
    );
  }, []);

  const patch = useCallback((id: string, change: Partial<PortalPhoto> | null) => {
    setPhotos((all) =>
      all
        ? change === null
          ? all.filter((p) => p.id !== id)
          : all.map((p) => (p.id === id ? { ...p, ...change } : p))
        : all,
    );
  }, []);

  const total = photos ? (staff ? livePhotos(photos) : clientVisiblePhotos(photos)).length : 0;

  return (
    <div className="stack">
      {mayAdd && (
        <AddPhotos
          sb={sb}
          project={project}
          renderer={services?.renderer ?? browserPhotoRenderer}
          archive={services?.archive}
          onAdded={reload}
        />
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!photos && !error && <Loading what="photos" />}
      {photos && total === 0 && (
        <div className="panel">
          <p className="body">
            {staff
              ? `No photos yet. Photos the field crew add in Teslyn Field appear here once they sync${mayAdd ? ', and you can add photos here' : ''}.`
              : 'No photos shared yet. Your survey company shares project photos here.'}
          </p>
        </div>
      )}
      {photos && total > 0 && (
        <>
          <div className="row row--wrap row--between">
            <SelectField<Filter>
              label="Category"
              value={filter}
              onChange={setFilter}
              options={[['all', 'All categories'], ...CATEGORY_OPTIONS]}
            />
            <p className="caption muted">
              {filter === 'all'
                ? plural(total, 'photo', 'photos')
                : `${shown.length} of ${plural(total, 'photo', 'photos')}`}
            </p>
          </div>
          {staff && archived && (
            <p className="caption muted">The project is archived: its photos are read only.</p>
          )}
          {staff && project.clientOrgId === null && (
            <p className="caption" role="note">
              No client organisation is linked to this project, so no client sees its photos, shared or not.
              Choose one under &ldquo;Client organisation&rdquo; at the top of the project page.
            </p>
          )}
          {staff && !archived && !mayShare && (
            <p className="caption muted">
              A manager, tenant admin or office staff shares photos with the client. Put a photo forward to
              ask for it.
            </p>
          )}
          {shown.length === 0 ? (
            <p className="body">No photos in this category.</p>
          ) : (
            <ul className="photo-grid" aria-label={`Photos of ${project.number}`}>
              {shown.map((p) => (
                <PhotoTile
                  key={p.id}
                  sb={sb}
                  photo={p}
                  staff={staff}
                  mayShare={mayShare}
                  mayPutForward={mayPutForward}
                  mayDelete={mayDelete}
                  mayAircraft={mayAircraft && Boolean(files?.get(p.id)?.has('photo_display'))}
                  mayCover={mayCover && Boolean(files?.get(p.id)?.has('photo_display'))}
                  onCover={coverChanged}
                  mayEdit={!archived}
                  uploaded={!staff || uploaded(files, p.id)}
                  thumb={thumbs.get(photoPath(p, 'thumb') ?? '') ?? null}
                  onOpen={() => setViewing(p)}
                  onChanged={patch}
                />
              ))}
            </ul>
          )}
        </>
      )}
      {viewing && (
        <PhotoViewer
          sb={sb}
          photo={viewing}
          staff={staff}
          hasOriginal={!staff || Boolean(files?.get(viewing.id)?.has('photo'))}
          hasDisplay={!staff || Boolean(files?.get(viewing.id)?.has('photo_display'))}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------- tile

function PhotoTile({
  sb,
  photo: p,
  staff,
  mayShare,
  mayPutForward,
  mayDelete,
  mayAircraft,
  mayCover,
  onCover,
  mayEdit,
  uploaded,
  thumb,
  onOpen,
  onChanged,
}: {
  readonly sb: SupabaseClient;
  readonly photo: PortalPhoto;
  readonly staff: boolean;
  readonly mayShare: boolean;
  readonly mayPutForward: boolean;
  readonly mayDelete: boolean;
  /** "Use as aircraft photo…": a tenant admin, manager or office staff, the display size in Teslyn Cloud (598, 664). */
  readonly mayAircraft: boolean;
  /** "Use for the weekly report cover" (665, 712): the same roles, a live project, the display size uploaded. */
  readonly mayCover: boolean;
  readonly onCover: (id: string, cover: boolean) => void;
  /** False for an archived project. */
  readonly mayEdit: boolean;
  readonly uploaded: boolean;
  readonly thumb: string | null;
  readonly onOpen: () => void;
  readonly onChanged: (id: string, change: Partial<PortalPhoto> | null) => void;
}): ReactElement {
  const title = photoTitle(p);
  const place = photoPlaceText(p);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [caption, setCaption] = useState(p.caption);
  const [category, setCategory] = useState<PhotoCategory>(p.category);
  const shared = Boolean(p.shared_at);
  const from = p.source === 'field' ? 'from Teslyn Field' : 'from Teslyn Cloud';
  const shareState = photoShareState(p);
  const state = !uploaded
    ? p.source === 'field'
      ? 'Uploading from Teslyn Field'
      : 'Upload not finished: add the photo again'
    : PHOTO_SHARE_WORDS[shareState];

  async function run(action: () => Promise<void>, after: () => void): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      await action();
      after();
    } catch (e: unknown) {
      setProblem(errText(e));
    } finally {
      setBusy(false);
    }
  }

  const captionError =
    caption.trim().length > CAPTION_MAX ? `A caption is at most ${CAPTION_MAX} characters.` : undefined;

  return (
    <li className="photo-tile">
      <button type="button" className="photo-tile__open" aria-label={`Open ${title}`} onClick={onOpen}>
        {thumb ? (
          <img className="photo-tile__img" src={thumb} alt="" loading="lazy" />
        ) : (
          <span className="photo-tile__none caption muted">
            {uploaded ? 'Thumbnail not available' : 'Not in Teslyn Cloud yet'}
          </span>
        )}
      </button>
      <div className="photo-tile__body">
        <p className="body photo-tile__title">{title}</p>
        <p className="caption">
          <span className="chip">{photoCategoryLabel(p.category)}</span>
        </p>
        <p className="caption data">{photoTakenText(p)}</p>
        <p className="caption data">{place ?? <span className="muted">No place recorded</span>}</p>
        {staff && (
          <>
            <p className="caption muted">
              {p.added_by_name ? `Added by ${p.added_by_name} · ${from}` : `Added ${from}`}
            </p>
            <p className="caption">
              {state}
              {uploaded && shareState === 'put_forward' && p.share_requested_by_name
                ? ` by ${p.share_requested_by_name}`
                : ''}
            </p>
            <label className="body row check">
              <input
                type="checkbox"
                aria-label={`Share ${title} with client`}
                checked={shared}
                disabled={!mayShare || busy || !uploaded}
                onChange={(e) => {
                  const on = e.target.checked;
                  void run(
                    () => setPhotoShared(sb, p.id, on),
                    () =>
                      onChanged(
                        p.id,
                        on
                          ? { shared_at: new Date().toISOString() }
                          : { shared_at: null, share_requested_at: null, share_requested_by_name: '' },
                      ),
                  );
                }}
              />
              Share with client
            </label>
            {mayPutForward && !shared && (
              <div className="row row--wrap">
                {shareState === 'put_forward' ? (
                  <Button
                    variant="quiet"
                    busy={busy}
                    aria-label={`Withdraw ${title}`}
                    onClick={() =>
                      void run(
                        () => setPhotoPutForward(sb, p.id, false),
                        () => onChanged(p.id, { share_requested_at: null, share_requested_by_name: '' }),
                      )
                    }
                  >
                    Withdraw
                  </Button>
                ) : (
                  <Button
                    variant="quiet"
                    busy={busy}
                    aria-label={`Put ${title} forward for sharing`}
                    onClick={() =>
                      void run(
                        () => setPhotoPutForward(sb, p.id, true),
                        () => onChanged(p.id, { share_requested_at: new Date().toISOString() }),
                      )
                    }
                  >
                    Put forward for sharing
                  </Button>
                )}
              </div>
            )}
            {mayEdit && !editing && !confirming && (
              <div className="row row--wrap">
                <Button variant="quiet" aria-label={`Edit ${title}`} onClick={() => setEditing(true)}>
                  Edit
                </Button>
                {mayDelete && (
                  <Button variant="quiet" aria-label={`Delete ${title}`} onClick={() => setConfirming(true)}>
                    Delete
                  </Button>
                )}
              </div>
            )}
            {staff && p.category === 'aircraft' && !editing && !confirming && (
              <UseForWeeklyCover
                sb={sb}
                photoId={p.id}
                title={title}
                current={p.weekly_cover === true}
                mayChange={mayCover}
                onChanged={onCover}
              />
            )}
            {mayAircraft && !editing && !confirming && (
              <UseAsAircraftPhoto
                sb={sb}
                tenantId={p.tenant_id}
                title={title}
                display={photoPath(p, 'display') ?? ''}
              />
            )}
            {editing && (
              <form
                className="stack"
                aria-label={`Edit ${title}`}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (captionError) return;
                  void run(
                    () => updatePhotoDetails(sb, p.id, { caption, category }),
                    () => {
                      onChanged(p.id, { caption: caption.trim(), category });
                      setEditing(false);
                    },
                  );
                }}
              >
                <TextField label="Caption" value={caption} onChange={setCaption} error={captionError} />
                <SelectField<PhotoCategory>
                  label="Category"
                  value={category}
                  onChange={setCategory}
                  options={CATEGORY_OPTIONS}
                />
                <div className="row row--wrap">
                  <Button type="submit" busy={busy}>
                    {busy ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    variant="quiet"
                    onClick={() => {
                      setEditing(false);
                      setCaption(p.caption);
                      setCategory(p.category);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
            {confirming && (
              <div className="stack" role="group" aria-label={`Delete ${title}?`}>
                <p className="body">
                  Delete {title}? It leaves the gallery and the maps, and the client no longer sees it. Its
                  files are kept.
                </p>
                <div className="row row--wrap">
                  <Button
                    busy={busy}
                    onClick={() =>
                      void run(
                        () => deletePhoto(sb, p.id),
                        () => onChanged(p.id, null),
                      )
                    }
                  >
                    {busy ? 'Deleting…' : 'Delete photo'}
                  </Button>
                  <Button variant="quiet" onClick={() => setConfirming(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        {problem && (
          <p className="notice notice--fail" role="alert">
            {problem}
          </p>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------------------------------- viewer

function PhotoViewer({
  sb,
  photo: p,
  staff,
  hasOriginal,
  hasDisplay,
  onClose,
}: {
  readonly sb: SupabaseClient;
  readonly photo: PortalPhoto;
  readonly staff: boolean;
  readonly hasOriginal: boolean;
  readonly hasDisplay: boolean;
  readonly onClose: () => void;
}): ReactElement {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // decision 643: the original may be kept in the organisation's Nextcloud (staff only)
  const [where, setWhere] = useState<OriginalPlace>('unknown');
  const title = photoTitle(p);
  const place = photoPlaceText(p);

  useEffect(() => {
    if (!staff) return undefined;
    let live = true;
    void photoOriginalPlace(sb, p.id).then((w) => live && setWhere(w));
    return () => {
      live = false;
    };
  }, [sb, p.id, staff]);

  useEffect(() => {
    panel.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let live = true;
    setUrl(null);
    setProblem(null);
    const path = photoPath(p, 'display');
    if (!hasDisplay || !path) {
      setProblem('The display size of this photo is not in Teslyn Cloud yet.');
      return undefined;
    }
    signedPhotoUrl(sb, path)
      .then((u) => live && setUrl(u))
      .catch((e: unknown) => live && setProblem(errText(e)));
    return () => {
      live = false;
    };
  }, [sb, p, hasDisplay]);

  async function download(): Promise<void> {
    const path = photoPath(p, 'original');
    if (!path) return;
    setBusy(true);
    setProblem(null);
    let objectUrl: string | null = null;
    try {
      const link = document.createElement('a');
      if (where === 'nextcloud') {
        // fetched from the Nextcloud through Teslyn Cloud, then saved under the photo's own name
        objectUrl = URL.createObjectURL(await fetchArchivedOriginal(sb, p.id));
        link.href = objectUrl;
        link.download = p.file_name;
      } else {
        link.href = await signedPhotoUrl(sb, path, p.file_name);
      }
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: unknown) {
      setProblem(errText(e));
    } finally {
      if (objectUrl) {
        const done = objectUrl;
        setTimeout(() => URL.revokeObjectURL(done), 60_000);
      }
      setBusy(false);
    }
  }

  return (
    <div className="photo-viewer" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div ref={panel} className="photo-viewer__panel stack">
        <div className="row row--between">
          <h2 id={titleId} className="heading">
            {title}
          </h2>
          <Button variant="quiet" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="photo-viewer__frame">
          {url ? (
            <img className="photo-viewer__img" src={url} alt={title} />
          ) : (
            !problem && <Loading what="the photo" />
          )}
        </div>
        <p className="caption">
          <span className="chip">{photoCategoryLabel(p.category)}</span>{' '}
          <span className="data">{photoTakenText(p)}</span>
          {place && (
            <>
              {' · '}
              <span className="data">{place}</span>
            </>
          )}
        </p>
        {staff && (
          <div className="row row--wrap">
            <Button busy={busy} disabled={!hasOriginal} onClick={() => void download()}>
              {busy ? 'Downloading…' : 'Download original'}
            </Button>
            <span className="caption muted">
              {hasOriginal
                ? `${p.file_name}, ${photoSizeText(p.size_bytes)}, ${p.width} × ${p.height} px`
                : photoOriginalPlaceText('not_uploaded')}
              {hasOriginal && where === 'nextcloud' && ` · ${photoOriginalPlaceText('nextcloud')}`}
            </span>
          </div>
        )}
        {problem && (
          <p className="notice notice--fail" role="alert">
            {problem}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------- adding

interface BatchResult {
  readonly added: number;
  readonly refused: readonly string[];
  readonly notices: readonly string[];
}

function AddPhotos({
  sb,
  project,
  renderer,
  archive,
  onAdded,
}: {
  readonly sb: SupabaseClient;
  readonly project: Pick<PortalProject, 'id' | 'tenantId'>;
  readonly renderer: PhotoRenderer;
  readonly archive: ArchivePhoto | undefined;
  readonly onAdded: () => void;
}): ReactElement {
  const titleId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<PhotoCategory>(DEFAULT_PHOTO_CATEGORY);
  const [caption, setCaption] = useState('');
  const [over, setOver] = useState(false);
  const [progress, setProgress] = useState<{ readonly at: number; readonly of: number } | null>(null);
  const [result, setResult] = useState<BatchResult | null>(null);
  const captionError =
    caption.trim().length > CAPTION_MAX ? `A caption is at most ${CAPTION_MAX} characters.` : undefined;

  async function addAll(list: readonly File[]): Promise<void> {
    if (list.length === 0 || progress || captionError) return;
    setResult(null);
    const refused: string[] = [];
    const notices: string[] = [];
    let added = 0;
    const deps = { sb, renderer, archive: archive ?? functionArchive(sb) };
    for (const [i, file] of list.entries()) {
      setProgress({ at: i + 1, of: list.length });
      const check = photoFileCheck(file.name, file.size);
      if (!check.ok) {
        refused.push(check.message);
        continue;
      }
      try {
        const r = await addPhoto(deps, project, file, { category, caption });
        added += 1;
        if (r.archiveNotice) notices.push(`${file.name}: ${r.archiveNotice}`);
      } catch (e: unknown) {
        refused.push(`Could not add ${file.name}: ${errText(e)}`);
      }
    }
    setProgress(null);
    setResult({ added, refused, notices });
    if (added > 0) onAdded();
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setOver(false);
    void addAll(Array.from(e.dataTransfer?.files ?? []));
  }

  return (
    <section className="panel stack" aria-labelledby={titleId}>
      <h2 id={titleId} className="heading">
        Add photos
      </h2>
      <div className="form-grid">
        <SelectField<PhotoCategory>
          label="Category"
          value={category}
          onChange={setCategory}
          options={CATEGORY_OPTIONS}
          disabled={Boolean(progress)}
        />
        <TextField
          label="Caption (optional)"
          value={caption}
          onChange={setCaption}
          error={captionError}
          disabled={Boolean(progress)}
        />
      </div>
      <div
        className={over ? 'photo-drop photo-drop--over' : 'photo-drop'}
        role="group"
        aria-label="Drop photos here"
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <p className="body">Drag photos here, or</p>
        <Button busy={Boolean(progress)} onClick={() => input.current?.click()}>
          Choose photos
        </Button>
        <input
          ref={input}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,.bmp"
          className="sr-only"
          tabIndex={-1}
          aria-label="Photo files"
          onChange={(e) => {
            const chosen = Array.from(e.target.files ?? []);
            e.target.value = '';
            void addAll(chosen);
          }}
        />
        <p className="caption muted">
          {PHOTO_ACCEPTED_TEXT}. The category and caption apply to every photo you add now. Photos stay
          private to staff until a manager or office staff shares them with the client.
        </p>
      </div>
      {progress && (
        <p className="body" role="status" aria-live="polite">
          Adding {progress.at} of {progress.of}…
        </p>
      )}
      {result && (
        <div className="stack" role="status" aria-live="polite">
          <p className="body">
            {result.added > 0 ? `Added ${plural(result.added, 'photo', 'photos')}.` : 'No photos were added.'}
          </p>
          {result.refused.length > 0 && (
            <div className="notice notice--fail">
              <p className="label">Not added</p>
              <ul>
                {result.refused.map((m) => (
                  <li key={m} className="body">
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.notices.length > 0 && (
            <div className="notice notice--warn">
              <p className="label">File archive</p>
              <ul>
                {result.notices.map((m) => (
                  <li key={m} className="body">
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
